/**
 * Copyright (c) 2019 Vendor Badge, LLC All Rights Reserved.
 *
 * Mobx Auth & Session Store
 */
import { configure, observable, decorate, action, computed } from 'mobx';
import { firebase } from 'containers/services/Firebase';
configure({ enforceActions: 'observed' });

class AuthStore {
  isLoading = false;
  isListening = false;
  isLoaded = false;
  isOnline = false;
  currentUser = null;
  claims = {};

  constructor(stores) {
    this._stores = stores;
    this.syncPresence = this.syncPresence.bind(this);

    /* Setup Auth listener */
    firebase.auth.onAuthStateChanged(user => {
      this.setField('isListening', true);
      this.setField('isLoading', true);
      this.stores.appStore.debug('Auth.firebase.auth.onAuthStateChanged() [service] Listening for auth changes...');
      // Update local auth state
      if (user) {
        // Update current user
        this.setField('currentUser', user);
        localStorage.setItem('authUser', JSON.stringify(user));
        this.stores.appStore.debug(
          'Auth.firebase.auth.onAuthStateChanged() [service] Auth change detected, currentUser',
          user
        );
        // Start user claims sync
        this.syncUserClaims(user);
      } else {
        // Not authorized, reset store
        localStorage.removeItem('authUser');
        this.setField('isLoading', false);
        if (this.isLoaded) {
          this.reset();
        }
      }
    });
  }

  /**
   * Syncs User Auth & DB Claims with Store
   */
  syncUserClaims = user => {
    firebase.data.ref(`users/${user.uid}/claims`).on(
      'value',
      async snapshot => {
        if (snapshot.exists()) {
          const authClaims = await this.fetchUserAuthClaims(user);
          const dbClaims = snapshot.val();
          const claims = { ...authClaims, ...dbClaims };
          this.setField('claims', claims);
          this.stores.appStore.debug('Auth.syncUserClaims [action] User claims updated', claims);
        }
        this.setField('isLoading', false);
        this.setField('isLoaded', true);
      },
      error => {
        if (error) {
          this.setField('isLoading', false);
          this.setField('isLoaded', false);
          this.stores.appStore.error('system', error);
        }
      }
    );
  };

  /**
   * Fetches a current authenticated user's custom auth claims
   */
  fetchUserAuthClaims = user => {
    return user.getIdTokenResult().then(token => {
      return token.claims;
    });
  };

  setField(fieldName, value) {
    this[fieldName] = value;
  }

  get stores() {
    return this._stores;
  }

  get isAuth() {
    return this.isLoaded && this.currentUser !== null ? true : false;
  }

  get isVerified() {
    return this.currentUser.emailVerified ? true : false;
  }

  get isCompanyUser() {
    if (this.claims && this.claims.isCompanyUser) {
      return true;
    } else {
      return false;
    }
  }

  get isLocationUser() {
    if (this.claims && this.claims.isLocationUser) {
      return true;
    } else {
      return false;
    }
  }

  get isVendorUser() {
    if (this.claims && this.claims.isVendorUser) {
      return true;
    } else {
      return false;
    }
  }

  get isSubscribed() {
    return this.claims.subscribed === 'true';
  }

  hasCompanyRole = (companyId, role) => {
    if (this.claims.hasOwnProperty('company') && this.claims.company.hasOwnProperty(companyId)) {
      return this.claims.company[companyId].role === role;
    } else {
      return false;
    }
  };

  hasLocationRole = (locationId, role) => {
    if (this.claims.hasOwnProperty('location') && this.claims.company.hasOwnProperty(locationId)) {
      return this.claims.location[locationId].role === role;
    } else {
      return false;
    }
  };

  /**
   * Checks to see if the current devices is on the user's authorized device list.
   */
  getIsAuthDevice(user) {
    const currentDeviceId = this.stores.device.thumbprint;
    return user.devices[currentDeviceId] ? true : false;
  }

  /**
   * Syncs user's online presence with database.
   */
  syncPresence() {
    const presenceRef = firebase.getUserPresenceRefById(this.currentUser.uid);
    const lastOnlineRef = firebase.getUserRefById(this.currentUser.uid).child('lastOnlineDtTm');
    const userConnectedRef = firebase.data.ref('.info/connected');

    const self = this;
    userConnectedRef.on('value', function(snapshot) {
      if (snapshot.val()) {
        // Set Disconnect trigger
        presenceRef
          .onDisconnect()
          .remove()
          .then(() => {
            // On disconnect, update database
            const timestamp = new Date().toISOString();
            lastOnlineRef.set(timestamp);
          });
        // User is online, set the presence
        presenceRef.set(true).then(() => {
          self.isOnline = true;
        });
      }
    });
  }

  /**
   * Signs user out of the application
   */
  signOut = () => {
    // Shut off any store listeners (all stores)
    localStorage.removeItem('authUser');

    // User Store
    firebase.data.ref(`users/${this.currentUser.uid}/ProfileInfo`).off();

    // Auth Store
    firebase.data.ref(`users/${this.currentUser.uid}/claims`).off();

    // Signout User
    firebase.auth
      .signOut()
      .then(() => {
        this.stores.resetStores();
      })
      .catch(error => {
        this.stores.appStore.error('system', error);
      });
  };

  reset() {
    this.isLoading = false;
    this.isListening = false;
    this.isLoaded = false;
    this.isOnline = false;
    this.currentUser = null;
    this.claims = {};
    this.stores.appStore.debug('Auth.reset() [action] Auth store has been reset.');
  }
}

decorate(AuthStore, {
  isLoading: observable,
  isListening: observable,
  isLoaded: observable,
  isOnline: observable,
  currentUser: observable,
  claims: observable,
  isAuth: computed,
  isVerified: computed,
  isCompanyUser: computed,
  isLocationUser: computed,
  isVendorUser: computed,
  isSubscribed: computed,
  syncPresence: action,
  hasCompanyRole: action,
  hasLocationRole: action,
  getIsAuthDevice: action,
  reset: action,
  setField: action,
  syncUserClaims: action,
  fetchUserAuthClaims: action,
});

export default AuthStore;
